import React from "react"
import { Form, FormType, ModalName } from "sharedTypes"
import { Box, Text, Flex } from "@chakra-ui/react"
import EditorSection from "components/Builder/components/EditorSection"
import Button from "components/Buttons/Button"
import { asDateWithTZ, asMoney } from "utilities/strings"
import useModal from "services/useModal"
import { useCurrentEvent } from "queries/events"
import useFeatureFlag from "services/featureFlags"
import { useLanguageTemplates } from "queries/languageTemplates"
import { capacityAppliesTo, isGuestStrategyEnabled } from "utilities/forms"
import { getGuestStrategyNameFromType } from "modals/FormForm/BasicInputs/GuestStrategy"

type Props = {
  form: Form
  onChange: (formData: Form) => void
  currency: string
}

const FormAttributesSection = ({ form, onChange, currency }: Props) => {
  const showModal = useModal()
  const {
    name,
    description,
    submissionsEndAt,
    limitPerSubmission,
    quantity,
    type,
    price,
    guestStrategy,
  } = form
  const { data: event } = useCurrentEvent()
  const { eventCapacity: eventCapacityEnabled } = useFeatureFlag()
  const { timezone } = event
  const { data: templates = [] } = useLanguageTemplates(event.id)
  // Matches the Language select's own "Use Event Default" blank-option label (BasicInputs.tsx)
  // — this row reflects the form's actual selection state, not a resolved value, so a form with
  // no override says so explicitly rather than just showing whatever that currently resolves to.
  const defaultTemplateName = templates.find(
    (template) => template.id === event.defaultLanguageTemplateId,
  )?.name ?? "English"
  const formTemplateName = templates.find((t) => t.id === form.languageTemplateId)?.name
  const languageTemplateName = formTemplateName ?? `Use Event Default (${defaultTemplateName})`

  const formattedSubmissionsEndAt = submissionsEndAt
    ? asDateWithTZ(submissionsEndAt, timezone, "PP, p")
    : null
  const maxPerText = `Max per ${type === FormType.Ticket ? "Order" : "Submission"}`
  const notRSVPOrSurvey = ![FormType.RSVP, FormType.Survey].includes(type)

  return (
    <EditorSection>
      <Box>
        <Text fontSize="xl" fontWeight="bold">{name}</Text>
        <Text color={!description ? "gray.400" : ""}>{description ?? "No description"}</Text>
      </Box>
      <Box mt={8}>
        <AttributeRow label="Closing Time" value={formattedSubmissionsEndAt || "Not specified"} inactive={!formattedSubmissionsEndAt} />
        {notRSVPOrSurvey && limitPerSubmission && (
          <AttributeRow
            label={maxPerText}
            value={limitPerSubmission}
          />
        )}
        {type !== FormType.Survey && (
          <AttributeRow
            label="Quantity"
            value={quantity || "Unlimited"}
            hint={eventCapacityEnabled && event.capacity && capacityAppliesTo(type) ? `Event capacity: ${event.capacity.toLocaleString()}` : undefined}
          />
        )}
        {Number(price) > 0 && <AttributeRow label="Price" value={`${currency} ${asMoney(price, currency)}`} /> }
        {isGuestStrategyEnabled(type) && <AttributeRow label="Guest Creation" value={getGuestStrategyNameFromType(guestStrategy)} />}
        <AttributeRow label="Language" value={languageTemplateName} />
        <Flex justifyContent="center" my={6}>
          <Button variant="outline" size="sm" onClick={() => showModal(ModalName.FormForm, { onSuccess: onChange, form })}>Edit Attributes</Button>
        </Flex>
      </Box>
    </EditorSection>
  )
}

type AttributeRowProps = {
  label: string;
  value: string | number;
  inactive?: boolean;
  hint?: string;
}

const AttributeRow = ({
  label, value, inactive, hint,
}: AttributeRowProps) => (
  <Flex my={2}>
    <Text width="50%">{label}</Text>
    <Box width="50%">
      <Text color={inactive ? "gray.400" : undefined}>{value}</Text>
      {hint && <Text fontSize="sm" color="gray.600">{hint}</Text>}
    </Box>
  </Flex>
)

export default FormAttributesSection
